/**
 * Payment Transfer Retry Administration Hooks
 * 
 * Provides admin-level tools for managing payment transfer retries and reconciliation.
 * These hooks enable administrators to monitor the payment transfer queue, manually
 * retry failed transfers, and reconcile payment statuses across the system.
 * 
 * **Payment Transfer Queue:**
 * - Tracks vendor payout transfers from escrow to Stripe Connect accounts
 * - Automatic retry with exponential backoff
 * - Manual admin intervention for permanently failed transfers
 * - Detailed error tracking and logging
 * 
 * **Queue Status States:**
 * - `pending`: Awaiting transfer initiation
 * - `processing`: Transfer in progress
 * - `completed`: Successfully transferred to vendor
 * - `failed`: Temporary failure, will retry automatically
 * - `failed_permanently`: Max retries exhausted, needs admin review
 * 
 * **Available Hooks:**
 * 1. `usePaymentTransferQueue` - View transfer queue with filters
 * 2. `useRetryPaymentTransfer` - Manually retry a failed transfer
 * 3. `useReconcilePayments` - Sync payment states with Stripe
 * 
 * **Reconciliation Process:**
 * - Compares database state with Stripe records
 * - Detects mismatches (e.g., completed in Stripe but pending in DB)
 * - Updates local records to match Stripe reality
 * - Identifies orphaned or stuck transfers
 * 
 * **Use Cases:**
 * - Monitor vendor payout processing
 * - Investigate failed transfer reasons
 * - Manually retry after fixing issues (e.g., invalid Stripe account)
 * - Audit trail for payment transfer lifecycle
 * - Financial reconciliation and reporting
 * 
 * @example
 * // Monitor transfer queue
 * const { data: queueItems } = usePaymentTransferQueue('failed_permanently');
 * 
 * queueItems?.map(item => (
 *   <Card>
 *     <h3>Job: {item.jobs?.title}</h3>
 *     <p>Error: {item.last_error}</p>
 *     <p>Retries: {item.retry_count}/{item.max_retries}</p>
 *     <Button onClick={() => retryTransfer({ jobId: item.job_id })}>
 *       Retry Transfer
 *     </Button>
 *   </Card>
 * ));
 * 
 * @example
 * // Manually retry failed transfer
 * const { mutate: retryTransfer } = useRetryPaymentTransfer();
 * 
 * retryTransfer({ jobId: 'job-123' });
 * 
 * @example
 * // Run reconciliation (admin dashboard)
 * const { mutate: reconcile, isPending } = useReconcilePayments();
 * 
 * <Button onClick={() => reconcile()} disabled={isPending}>
 *   Reconcile All Payments
 * </Button>
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';

export interface PaymentTransferQueueItem {
  id: string;
  job_id: string;
  payment_transaction_id: string;
  completion_id: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'failed_permanently';
  retry_count: number;
  max_retries: number;
  next_retry_at: string | null;
  last_error: string | null;
  last_attempt_at: string | null;
  created_at: string;
  updated_at: string;
  jobs?: {
    public_id: string;
    title: string;
    status: string;
    payment_status: string;
  };
  payment_transactions?: {
    vendor_payout_cents: number;
    total_amount_cents: number;
    status: string;
  };
}

export const usePaymentTransferQueue = (statusFilter?: string) => {
  return useQuery({
    queryKey: ['payment-transfer-queue', statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('payment_transfer_queue')
        .select(`
          *,
          jobs (
            public_id,
            title,
            status,
            payment_status
          ),
          payment_transactions (
            vendor_payout_cents,
            total_amount_cents,
            status
          )
        `)
        .order('created_at', { ascending: false });

      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as PaymentTransferQueueItem[];
    },
  });
};

export const useRetryPaymentTransfer = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ queueId }: { queueId: string }) => {
      const { data, error } = await invokeEdgeFunction('retry-payment-transfer', {
        body: { queueId },
        callerOperation: 'useRetryPaymentTransfer.mutate',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Response format from approve-and-transfer-payout: { status, transferId?, amount?, error? }
      const result = data as { 
        status: 'success' | 'error'; 
        error?: string;
        transferId?: string;
        amount?: number;
        message?: string;
      };
      
      if (result.status === 'success') {
        toast({
          title: 'Transfer Successful',
          description: result.transferId 
            ? `Payout completed. Transfer ID: ${result.transferId}`
            : result.message || 'Vendor payout has been processed.',
        });
      } else {
        toast({
          title: 'Transfer Failed',
          description: result.error || 'Unknown error occurred',
          variant: 'destructive',
        });
      }
      
      queryClient.invalidateQueries({ queryKey: ['payment-transfer-queue'] });
      queryClient.invalidateQueries({ queryKey: ['payment-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['payment-approvals'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Retry Failed',
        description: error.message || 'Failed to retry payment transfer',
        variant: 'destructive',
      });
    },
  });
};

export const useReconcilePayments = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await invokeEdgeFunction('reconcile-payment-status', {
        callerOperation: 'useReconcilePayments.mutate',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      const result = data as { issuesFound?: number };
      const issueCount = result?.issuesFound || 0;
      toast({
        title: 'Reconciliation Complete',
        description: issueCount > 0 
          ? `Found ${issueCount} issue(s) that need attention.`
          : 'No payment issues detected.',
      });
      queryClient.invalidateQueries({ queryKey: ['payment-transfer-queue'] });
      queryClient.invalidateQueries({ queryKey: ['payment-transactions'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Reconciliation Failed',
        description: error.message || 'Failed to reconcile payment status',
        variant: 'destructive',
      });
    },
  });
};
